import { create } from "zustand";

/**
 * Keyboard State Store
 *
 * Tracks modifier key "armed" state for one-click trading.
 * After an order is placed, the modifier key must be released (keyup)
 * before another order can be created with the same key.
 *
 * Flow:
 * 1. Initial state: all keys armed (true)
 * 2. User holds Shift + clicks → order created, shiftArmed = false
 * 3. User clicks again (still holding Shift) → blocked (shiftArmed is false)
 * 4. User releases Shift → shiftArmed = true
 * 5. User holds Shift + clicks → order created, shiftArmed = false
 */
export const useKeyboardStateStore = create((set) => ({
  // Track if key was released since last order (armed for next order)
  shiftArmed: true,
  ctrlArmed: true,
  metaArmed: true,

  // Track which key was blocked on last click attempt (for showing toast)
  blockedKey: null, // 'Shift', 'Ctrl', or 'Cmd'

  /**
   * Called on keyup - arm the key for next order and clear blocked state
   * @param {string} key - 'Shift', 'Control', or 'Meta'
   */
  armKey: (key) =>
    set((state) => {
      switch (key) {
        case "Shift":
          return { shiftArmed: true, blockedKey: null };
        case "Control":
          return { ctrlArmed: true, blockedKey: null };
        case "Meta":
          return { metaArmed: true, blockedKey: null };
        default:
          return state;
      }
    }),

  /**
   * Called after order placed - disarm until next keyup
   * @param {string} key - 'Shift', 'Control', or 'Meta'
   */
  disarmKey: (key) =>
    set((state) => {
      switch (key) {
        case "Shift":
          return { shiftArmed: false };
        case "Control":
          return { ctrlArmed: false };
        case "Meta":
          return { metaArmed: false };
        default:
          return state;
      }
    }),

  /**
   * Called when user clicks with a disarmed key - show blocked message
   * @param {string} keyName - Display name: 'Shift', 'Ctrl', or 'Cmd'
   */
  setBlockedKey: (keyName) => set({ blockedKey: keyName }),

  /**
   * Clear the blocked key message
   */
  clearBlockedKey: () => set({ blockedKey: null }),
}));
